<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { computed, inject, onUnmounted, ref, type Ref, type ShallowRef, watch } from 'vue'

import { useUiStore } from '@/stores/uiStore'
import { createMapMarker } from '@/utils/mapMarkerElement'
import { Z_INDEX } from '@/utils/zIndex'

type SmartSearchPin = {
  id: string
  lat: number
  lon: number
  title: string
  subtitle?: string | null
}

const props = defineProps<{
  pin: SmartSearchPin | null
}>()

const emit = defineEmits<{
  remove: []
}>()

const uiStore = useUiStore()
const mapInstance = inject<ShallowRef<maplibregl.Map | null>>('mapInstance')
const mapLoaded = inject<Ref<boolean>>('mapLoaded')

const marker = ref<maplibregl.Marker | null>(null)
let isMoveListenerBound = false
const contextMenu = ref<{
  visible: boolean
  x: number
  y: number
}>({
  visible: false,
  x: 0,
  y: 0,
})

const pinLabel = computed(() => props.pin?.title || 'Pinned location')

const closeContextMenu = () => {
  contextMenu.value.visible = false
}

const suppressNextMapClick = () => {
  uiStore.suppressNextMapClick()
}

const removePin = () => {
  closeContextMenu()
  suppressNextMapClick()
  emit('remove')
}

const openContextMenu = (clientX: number, clientY: number) => {
  const map = mapInstance?.value
  if (!map) return

  const rect = map.getContainer().getBoundingClientRect()
  contextMenu.value = {
    visible: true,
    x: clientX - rect.left,
    y: clientY - rect.top,
  }
}

const contextMenuAbsolutePos = computed(() => {
  const map = mapInstance?.value
  if (!map) return { x: 0, y: 0 }

  const rect = map.getContainer().getBoundingClientRect()
  return {
    x: rect.left + contextMenu.value.x,
    y: rect.top + contextMenu.value.y,
  }
})

const bindTouchContextMenu = (touchArea: HTMLDivElement) => {
  let activeTouchId: number | null = null
  let startX = 0
  let startY = 0
  let touchMoved = false

  const resetTouchState = () => {
    activeTouchId = null
    touchMoved = false
    touchArea.style.opacity = '1'
  }

  const resolveTrackedTouch = (touches: TouchList) => {
    if (activeTouchId === null) return null
    return Array.from(touches).find((touch) => touch.identifier === activeTouchId) ?? null
  }

  touchArea.addEventListener('touchstart', (event) => {
    const touch = event.changedTouches[0]
    if (!touch) return

    activeTouchId = touch.identifier
    startX = touch.clientX
    startY = touch.clientY
    touchMoved = false
    touchArea.style.opacity = '0.8'
  })

  touchArea.addEventListener('touchmove', (event) => {
    const touch = resolveTrackedTouch(event.touches)
    if (!touch) return

    if (Math.hypot(touch.clientX - startX, touch.clientY - startY) >= 8) {
      touchMoved = true
      touchArea.style.opacity = '1'
    }
  })

  touchArea.addEventListener('touchend', (event) => {
    const touch = resolveTrackedTouch(event.changedTouches)
    if (!touch) {
      resetTouchState()
      return
    }

    const shouldOpenMenu = !touchMoved
    resetTouchState()
    if (!shouldOpenMenu) return

    event.preventDefault()
    event.stopPropagation()
    suppressNextMapClick()
    openContextMenu(touch.clientX, touch.clientY)
  })

  touchArea.addEventListener('touchcancel', resetTouchState)
}

const createMarkerElement = () => {
  const container = document.createElement('div')
  container.className = 'smart-search-pin-marker relative'
  container.style.zIndex = Z_INDEX.MAP_MARKERS_WAYPOINT.toString()

  const touchArea = document.createElement('div')
  touchArea.className = 'touch-area'
  touchArea.style.position = 'absolute'
  touchArea.style.width = '48px'
  touchArea.style.height = '72px'
  touchArea.style.top = '-64px'
  touchArea.style.left = '-24px'
  touchArea.style.borderRadius = '50%'
  touchArea.style.cursor = 'pointer'
  touchArea.style.background = 'transparent'
  touchArea.setAttribute('aria-label', `Remove pin for ${pinLabel.value}`)

  const pill = document.createElement('div')
  pill.className =
    'pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-full border border-rose-200 bg-white/95 px-3 py-1.5 text-xs font-medium text-rose-700 shadow-lg backdrop-blur-sm whitespace-nowrap'
  pill.style.left = '0px'
  pill.style.top = '-52px'
  pill.style.zIndex = '10'
  pill.textContent = pinLabel.value

  const pinBody = document.createElement('div')
  pinBody.className =
    'pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-full bg-rose-600 text-white shadow-[0_10px_25px_rgba(190,24,93,0.35)] ring-2 ring-white'
  pinBody.style.left = '0px'
  pinBody.style.top = '-8px'
  pinBody.style.width = '34px'
  pinBody.style.height = '34px'
  pinBody.style.display = 'flex'
  pinBody.style.alignItems = 'center'
  pinBody.style.justifyContent = 'center'
  pinBody.style.zIndex = '30'

  const pinStem = document.createElement('div')
  pinStem.className =
    'pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-full bg-rose-600 ring-2 ring-white'
  pinStem.style.left = '0px'
  pinStem.style.top = '12px'
  pinStem.style.width = '8px'
  pinStem.style.height = '18px'
  pinStem.style.zIndex = '20'

  const pinTip = document.createElement('div')
  pinTip.className =
    'pointer-events-none absolute -translate-x-1/2 -translate-y-full rotate-45 rounded-[3px] bg-rose-600 ring-2 ring-white'
  pinTip.style.left = '0px'
  pinTip.style.top = '24px'
  pinTip.style.width = '12px'
  pinTip.style.height = '12px'
  pinTip.style.zIndex = '20'

  const pinCenter = document.createElement('div')
  pinCenter.className = 'pointer-events-none rounded-full bg-white/95 shadow-inner'
  pinCenter.style.width = '12px'
  pinCenter.style.height = '12px'
  pinBody.appendChild(pinCenter)

  container.appendChild(touchArea)
  container.appendChild(pill)
  container.appendChild(pinBody)
  container.appendChild(pinStem)
  container.appendChild(pinTip)

  touchArea.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    removePin()
  })

  touchArea.addEventListener('contextmenu', (event) => {
    event.preventDefault()
    event.stopPropagation()
    suppressNextMapClick()
    openContextMenu(event.clientX, event.clientY)
  })

  bindTouchContextMenu(touchArea)

  return container
}

const teardownMarker = () => {
  marker.value?.remove()
  marker.value = null
  closeContextMenu()
}

const syncMarker = () => {
  const map = mapInstance?.value
  if (!mapLoaded?.value || !map || !props.pin) {
    teardownMarker()
    return
  }

  teardownMarker()
  marker.value = createMapMarker({
    element: createMarkerElement(),
    anchor: 'bottom',
  })
    .setLngLat([props.pin.lon, props.pin.lat])
    .addTo(map)

  if (!isMoveListenerBound) {
    map.on('movestart', closeContextMenu)
    isMoveListenerBound = true
  }
}

watch([() => props.pin, () => mapLoaded?.value, () => mapInstance?.value], () => {
  syncMarker()
})

onUnmounted(() => {
  const map = mapInstance?.value
  if (map && isMoveListenerBound) {
    map.off('movestart', closeContextMenu)
    isMoveListenerBound = false
  }
  teardownMarker()
})
</script>

<template>
  <div v-if="contextMenu.visible">
    <div
      class="fixed inset-0 z-[39]"
      @click="closeContextMenu"
      @contextmenu.prevent="closeContextMenu"
    />
    <div
      class="fixed z-40 min-w-[180px] overflow-hidden rounded-xl border border-gray-200 bg-white/95 p-1 shadow-2xl backdrop-blur"
      :style="{
        left: `${contextMenuAbsolutePos.x}px`,
        top: `${contextMenuAbsolutePos.y}px`,
      }"
    >
      <div class="px-3 py-2 text-xs text-gray-500">
        <div class="font-medium text-gray-900">{{ pin?.title }}</div>
        <div v-if="pin?.subtitle" class="mt-0.5 text-gray-500">{{ pin.subtitle }}</div>
      </div>
      <button
        class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-rose-700 transition-colors hover:cursor-pointer hover:bg-rose-50"
        @click="removePin"
      >
        Remove pin
      </button>
    </div>
  </div>
</template>
